import { CtaLink } from "@/components/ui";
import { useT } from "@/lib/i18n";
import { useLeadTimes, type LeadTimes } from "@/lib/settings";

interface Step {
  tag: string; // kort label voor de smalle (ingeklapte) strip
  title: string;
  body: string;
}

const STEPS: Step[] = [
  { tag: "Glas", title: "Kies uw glas", body: "Selecteer uw glassoort, kleur/uitvoering en de dikte. De prijs per m² ziet u er meteen bij." },
  { tag: "Vorm", title: "Kies de vorm", body: "Rechthoek of een speciale vorm (cirkel, ovaal, schuine hoek). Een eventuele vormtoeslag ziet u direct." },
  { tag: "Maten", title: "Vul uw maten in", body: "Breedte × hoogte in mm — geef altijd de glasmaat op, niet de sponningmaat. Wij maken exact wat u invult." },
  { tag: "Bewerkingen", title: "Bewerkingen naar wens", body: "Randafwerking (van standaard snijkant tot gepolijst, verstek of facet) en boringen, uitsparingen en ronde hoeken." },
  { tag: "Prijs", title: "Direct de prijs, veilig besteld", body: "Live prijs (incl./excl. btw) met alle toeslagen, een gratis veiligheidscheck en daarna verzekerd verzonden." },
];

/**
 * De levertijden in het eerste antwoord komen uit de admin (Instellingen → Levertijden), niet uit
 * deze tekst. Ze stonden hier als "4 tot 6 weken" en "2 tot 3 weken" ingetypt en liepen daarmee
 * stil uit de pas met de rest van de site — en met de FAQ-structuurdata die Google uitleest.
 */
function bouwFaqs(leadTimes: LeadTimes): { q: string; a: string }[] {
  return [
  {
    q: "Wat is de levertijd en hoe wordt mijn glas bezorgd?",
    a: `Omdat we met verschillende productgroepen werken, verschilt de levertijd. Glasartikelen en beslag leveren we grotendeels direct uit eigen voorraad. Maatwerkglas en BENG-glas hebben een specifieke productietijd, omdat alles tot op de millimeter voor u op maat wordt gemaakt. Houd voor BENG-glas rekening met ${leadTimes.vacuumglas} en voor overig maatwerkglas met ${leadTimes.glasOpMaat}. Om het transport zo veilig mogelijk te laten verlopen, rijden we met vaste routes door Nederland en Vlaanderen. De exacte levertijd en de vaste routedag in uw regio ziet u op onze pagina over levering en bezorging.`,
  },
  {
    q: "Hoe weet ik zeker dat ik de juiste maten doorgeef?",
    a: "Glas bestellen is millimeterwerk, maar met de juiste voorbereiding gaat er weinig mis. De basisregel: meet altijd de strakke sponningmaat (de binnenkant van het kozijn) op meerdere punten, want kozijnen zijn zelden kaarsrecht. Neem de kleinste gemeten maat en haal daar de benodigde sponning- of glasaftrek vanaf, zodat het glas rondom de juiste speling heeft om te 'werken'. Omdat de aftrek per situatie verschilt, hebben we het stap voor stap uitgeschreven in onze kennisbank. Komt u er niet uit? Bel of mail ons; via een foto of tekening kijken we graag even mee.",
  },
  {
    q: "Kan ik als particulier ook bij Glastotaal terecht?",
    a: "Absoluut. We werken veel samen met professionals en grote bouwprojecten, maar helpen particulieren met precies hetzelfde plezier. Of u nu uw woning verduurzaamt met BENG-glas of een glazen spatwand voor de keuken zoekt: u bestelt als consument gewoon direct online. Juist omdat u niet dagelijks glas inkoopt, hebben we de configurator zo begrijpelijk mogelijk gemaakt — dezelfde professionele kwaliteit en materialen als de vakman, met heldere uitleg en zonder addertjes. Komt u er niet uit? Onze specialisten helpen u telefonisch of per mail op weg.",
  },
  {
    q: "Wat als mijn glas tijdens het transport breekt?",
    a: "Daar hoeft u zich geen zorgen over te maken: wij dragen het volledige risico tot het glas veilig bij u voor de deur staat. Ontstaat er onverhoopt een kras, beschadiging of breuk tijdens transport, dan lossen we dat direct en kosteloos op. We bestellen meteen een nieuw, perfect passend exemplaar en leveren dat zo snel mogelijk. Het enige wat we vragen: controleer uw bestelling bij aankomst even samen met de chauffeur.",
  },
  {
    q: "Komen jullie het glas ook inmeten en zetten?",
    a: "Glastotaal.nl is een webshop; we leveren het glas in principe af tot aan de deur. Wilt u de montage liever overlaten aan een professional? Dat kan via onze vaste montagepartner. U stelt uw project samen in de configurator en geeft aan dat uw montage wenst; vanaf dat moment neemt onze montagepartner het over, brengt u in contact met de planners en maakt een aparte offerte op maat voor inmeten en plaatsen. Houd er rekening mee dat de montagetarieven afwijken van de kale materiaalprijzen in de webshop, omdat er met eigen uren- en voorrijtarieven wordt gerekend.",
  },
  ];
}

/** SEO/GEO: HowTo + FAQPage structured data, opgebouwd uit dezelfde STEPS/FAQS. */
export const configuratorHowToJsonLd = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  name: "Glas op maat samenstellen",
  description: "In vijf stappen uw glas op maat samenstellen en bestellen bij Glastotaal: kies uw glas, kies de vorm, vul uw maten in, kies bewerkingen en bestel met directe prijs.",
  step: STEPS.map((s, i) => ({ "@type": "HowToStep", position: i + 1, name: s.title, text: s.body })),
};

export function configuratorFaqJsonLd(leadTimes: LeadTimes): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: bouwFaqs(leadTimes).map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

/**
 * Homepage-sectie: de vijf stappen als eenvoudige, statische kaartjes.
 *
 * Stond hier eerder als interactieve Supademo-rondleiding (iframe naar een losse app). Die woog
 * zwaarder dan 'ie opleverde: een `loading="lazy"`-iframe die door de browsers eigen preload-afstand
 * alsnog al ~0,5s na page load begon te laden — dus vrijwel elke bezoeker kreeg 'm meteen mee, ook
 * degene die de homepage alleen even scande. Vijf statische kaartjes vertellen exact hetzelfde
 * verhaal zonder extra netwerkverzoek, extern script of derde-partij-JS.
 */
export function ConfiguratorExplainer() {
  const t = useT();
  return (
    <section aria-labelledby="hoe-werkt-het" className="mx-auto max-w-[1600px] px-4 pt-14">
      <h2 id="hoe-werkt-het" className="text-lg font-bold text-primary-900 sm:text-2xl">{t("Zo stelt u uw glas op maat samen")}</h2>
      <p className="mt-2 max-w-2xl text-primary-600">
        {t("Klik zelf door de configurator — in een minuut ziet u hoe u van idee naar besteld gaat, op de millimeter en met direct de juiste prijs.")}
      </p>

      <ol className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {STEPS.map((s, i) => (
          <li key={s.title} className="rounded-2xl border border-primary-100 bg-white p-5 shadow-sm">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-accent-700 text-sm font-bold text-white">{i + 1}</span>
            <p className="mt-3 font-bold text-primary-900">{t(s.title)}</p>
            <p className="mt-1 text-sm text-primary-600">{t(s.body)}</p>
          </li>
        ))}
      </ol>

      <div className="mt-8">
        <CtaLink to="/glassoorten">{t("Samenstellen")} →</CtaLink>
      </div>
    </section>
  );
}

/** FAQ-sectie (los, zodat 'ie onder de beslag-teaser kan staan). Native accordion:
 *  content blijft in de DOM voor SEO/GEO (zie configuratorFaqJsonLd). */
export function ConfiguratorFaq() {
  const t = useT();
  const FAQS = bouwFaqs(useLeadTimes());
  return (
    <section aria-label="Veelgestelde vragen" className="mx-auto max-w-[1600px] px-4 pt-14">
      <h2 className="text-lg font-bold text-primary-900 sm:text-2xl">{t("Veelgestelde vragen")}</h2>
      <div className="mt-6 divide-y divide-primary-100 overflow-hidden rounded-2xl border border-primary-100 bg-white">
        {FAQS.map((f) => (
          <details key={f.q} className="group">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-3 p-4 font-medium text-primary-900 sm:p-5">
              {t(f.q)}
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="shrink-0 text-primary-600 transition-transform group-open:rotate-180">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </summary>
            <p className="px-4 pb-4 text-sm text-primary-600 sm:px-5 sm:pb-5">{t(f.a)}</p>
          </details>
        ))}
      </div>
    </section>
  );
}
